import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IntlProvider } from 'react-intl';

import { useIsFeatureEnabled } from '@xarvio/frontend-core-state/lib/featureFlags/useIsFeatureEnabled';

import { HeaderLoadingSkeleton } from '../components/shared/Header/HeaderLoadingSkeleton';
import { NewStatusPageLoading } from '../pages/NewStatusPage/NewStatusPageLoading';
import type { NewStatusPageLoadingPanel } from '../pages/NewStatusPage/NewStatusPageLoadingPanel';
import { TableStatusPageLoading } from '../pages/TableStatusPage/TableStatusPageLoading';
import type { StatusPageVariant } from './PrivateRoutes/statusPageVariant';
import { StatusInitialLoadingContext } from './StatusInitialLoadingContext';
import {
  getStatusLoadingRoute,
  isNewStatusLoadingRoute,
  isTableStatusLoadingRoute,
} from './statusLoadingRoute';
import styles from './StatusInitialLoadingShellProvider.module.scss';

const EMPTY_MESSAGES = {};
const ignoreIntlError = () => undefined;

const TableStatusPageLoadingWithIntl = () => (
  <IntlProvider
    locale={'en'}
    messages={EMPTY_MESSAGES}
    onError={ignoreIntlError}
  >
    <TableStatusPageLoading />
  </IntlProvider>
);

export const TableStatusInitialLoadingShell = () => {
  return (
    <div className={styles.loadingShell}>
      <HeaderLoadingSkeleton />
      <TableStatusPageLoadingWithIntl />
    </div>
  );
};

export const TableStatusContentLoadingShell = () => {
  return (
    <div className={styles.contentLoadingShell}>
      <TableStatusPageLoadingWithIntl />
    </div>
  );
};

export const StatusInitialLoadingShell = ({
  loadingRoute,
}: {
  loadingRoute: NewStatusPageLoadingPanel;
}) => {
  return (
    <div className={styles.contentLoadingShell}>
      <IntlProvider
        locale={'en'}
        messages={EMPTY_MESSAGES}
        onError={ignoreIntlError}
      >
        <NewStatusPageLoading withTitle={false} panel={loadingRoute} />
      </IntlProvider>
    </div>
  );
};

export const StatusInitialLoadingShellProvider = ({
  children,
  statusPageVariant,
}: React.PropsWithChildren<{ statusPageVariant: StatusPageVariant }>) => {
  const { pathname } = useLocation();
  const loadingRoute = getStatusLoadingRoute(pathname);
  const isStatusLoadingRoute = loadingRoute !== undefined;
  const [wasStatusLoadingRoute, setWasStatusLoadingRoute] =
    useState(isStatusLoadingRoute);
  const [isStatusInitialLoadingReady, setIsStatusInitialLoadingReady] =
    useState(false);
  const forceSkeletons = useIsFeatureEnabled('FORCE_SKELETONS');

  if (isStatusLoadingRoute !== wasStatusLoadingRoute) {
    setWasStatusLoadingRoute(isStatusLoadingRoute);

    if (isStatusLoadingRoute) {
      setIsStatusInitialLoadingReady(false);
    }
  }

  const markStatusInitialLoadingReady = useCallback(() => {
    setIsStatusInitialLoadingReady(true);
  }, []);

  const shouldShowNewStatusShell =
    isNewStatusLoadingRoute(loadingRoute) && statusPageVariant !== 'table';
  const shouldShowTableStatusShell =
    isTableStatusLoadingRoute(loadingRoute) && statusPageVariant !== 'new';
  const isLoadingShellVisible =
    (forceSkeletons || !isStatusInitialLoadingReady) &&
    (shouldShowNewStatusShell || shouldShowTableStatusShell);

  return (
    <StatusInitialLoadingContext value={markStatusInitialLoadingReady}>
      {children}
      {isLoadingShellVisible && shouldShowNewStatusShell && (
        <StatusInitialLoadingShell loadingRoute={loadingRoute} />
      )}
      {isLoadingShellVisible && shouldShowTableStatusShell && (
        <TableStatusContentLoadingShell />
      )}
    </StatusInitialLoadingContext>
  );
};
